import React from "react";
import { motion, useInView, useScroll } from "framer-motion";

const RotatedSlider = ({ children }) => {
  const containerRef = React.useRef(null);
  const [scrollDirection, setScrollDirection] = React.useState("down");
  const [isMobile, setIsMobile] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const { scrollY } = useScroll();
  const lastScrollY = React.useRef(0);

  // Avoid state updates with ref values that don't trigger re-renders
  const childrenRef = React.useRef(children);
  const isFirstRenderRef = React.useRef(true);

  // Keep the original useInView settings
  const isInView = useInView(containerRef, {
    once: false,
    amount: 0.3,
    margin: "0px 0px -10% 0px",
  });

  // Ensure calculations are accurate before animation
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setReady(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  // Track scroll direction
  React.useEffect(() => {
    const unsubscribe = scrollY.on("change", (latest) => {
      const direction = latest > lastScrollY.current ? "down" : "up";
      setScrollDirection(direction);
      lastScrollY.current = latest;
    });
    return unsubscribe;
  }, [scrollY]);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Update childrenRef when children change
  React.useEffect(() => {
    // Skip the first render to avoid immediate re-render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    childrenRef.current = children;
  }, [children]);

  // Safety checks for calculations
  const totalItems = React.Children.count(childrenRef.current) || 1;
  const maxRotation = 45;
  const center = (totalItems - 1) / 2;

  const isMiddleElement = (index) => {
    if (totalItems % 2 === 0) {
      return index === Math.floor(center) || index === Math.ceil(center);
    }
    return index === center;
  };

  const getMobileAnimationOrder = (index) => {
    if (scrollDirection === "down") {
      return index;
    } else {
      return totalItems - 1 - index;
    }
  };

  const getDesktopAnimationOrder = (index) => {
    if (isMiddleElement(index)) return 0;
    const distanceFromCenter = Math.abs(index - center);
    return distanceFromCenter;
  };

  const getMobileInitialRotation = (index) => {
    return index % 2 === 0 ? -90 : 90;
  };

  const getMobileInitialX = (index) => {
    return index % 2 === 0 ? -300 : 300;
  };

  const getRotation = (index) => {
    if (!isMobile) {
      const relativePosition = (index - center) / (center || 1);
      return maxRotation * relativePosition;
    }
    return getMobileInitialRotation(index);
  };

  // Return explicit numeric value
  const getXValue = (index) => {
    if (isMobile) {
      return getMobileInitialX(index);
    } else {
      return getRotation(index) * 2;
    }
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = (index) => {
    const isMiddle = !isMobile && isMiddleElement(index);
    const comingFromBottom = scrollDirection === "up";

    // Calculate delay
    const delay = isMobile
      ? getMobileAnimationOrder(index) * 0.2
      : isMiddle
      ? 0
      : 0.2 + getDesktopAnimationOrder(index) * 0.1;

    // Use explicit numeric values for all animations
    return {
      hidden: {
        opacity: 0,
        rotate: getRotation(index),
        x: getXValue(index), // Numeric values only
        y: isMiddle ? (comingFromBottom ? -100 : 100) : 0,
        scale: 0.8,
      },
      visible: {
        opacity: 1,
        rotate: 0,
        x: 0,
        y: 0,
        scale: 1,
        transition: {
          type: "spring",
          bounce: 0.4,
          duration: 1,
          delay,
          y: {
            type: "spring",
            bounce: 0.3,
            duration: 0.8,
          },
        },
      },
    };
  };

  // Process the children list exactly once - don't recreate this on every render
  // This is critical to prevent remounts!
  const processedChildren = React.useMemo(() => {
    return React.Children.map(childrenRef.current, (child) => {
      // Only modify AnimatedCircularProgress or GrowthBarGraph - ignore everything else
      if (
        child?.type?.name === "AnimatedCircularProgress" ||
        child?.type?.name === "GrowthBarGraph"
      ) {
        return React.cloneElement(child, { forceAnimation: isInView });
      }

      // Return anything else completely untouched
      return child;
    });
  }, [isInView]); // Only depend on isInView

  return (
    <motion.div
      ref={containerRef}
      className={`
        w-full 
        py-0 md:py-12 
        ${isMobile ? "overflow-hidden" : ""}
      `}
      variants={containerVariants}
      initial="hidden"
      animate={isInView && ready ? "visible" : "hidden"}
    >
      <div
        className={`
          ${isMobile ? "grid grid-cols-1 auto-rows-auto" : "flex flex-row"}
          justify-center 
          items-center 
          ${isMobile ? "gap-6 mb-12 md:mb-0" : "gap-8"}
          ${isMobile ? "mx-auto" : ""}
        `}
      >
        {React.Children.map(processedChildren, (child, index) => (
          <motion.div
            key={`slider-item-${index}`} // Stable key that doesn't change
            variants={itemVariants(index)}
            className={`
              ${isMobile ? "mx-auto" : ""}
              ${isMobile ? "w-[250px]" : "w-full max-w-[300px]"}
            `}
            style={{
              opacity: 0,
            }}
          >
            {child}
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default React.memo(RotatedSlider);
